package diana.soleil.design.iraniancanadienbazzar.feature.onboarding

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.ime
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppleLogo
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.GoogleLogo
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BrandMark
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppTextField
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_apple
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_brand
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_continue_guest
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_divider
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_email_label
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_email_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_error_email_in_use
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_error_generic
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_error_invalid_credentials
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_error_invalid_email
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_error_network
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_forgot
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_google
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_join_now
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_reset_sent
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_logo_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_network_badge
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_new_here
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_password_label
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_password_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_show_password_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_subtitle
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_submit
import org.jetbrains.compose.resources.stringResource

/**
 * A non-`null` status renders the inline banner under the submit button: every value except
 * [ResetSent] is an error (mapped from `AppError` by the sign-in route); [ResetSent] is the success
 * confirmation for the password-reset email.
 */
enum class SignInStatus { InvalidCredentials, InvalidEmail, EmailInUse, Network, Generic, ResetSent }

/**
 * Sign In — Stitch `b7357c0f` (Directory & Community System). Tokens + string resources only.
 * Navigation-agnostic; the host owns auth and passes [isLoading]/[status]. Field state stays local;
 * the email is handed back to [onForgotPassword] and to [onSignUp] (lightweight registration with the
 * same fields until the dedicated Join flow lands).
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun SignInScreen(
    onSignIn: (email: String, password: String) -> Unit,
    onSignUp: () -> Unit,
    // Null hides the button: the social credential flows aren't wired yet, and a button that
    // does nothing reads as "broken app". Reintroduce per-platform as each flow ships.
    onGoogle: (() -> Unit)?,
    onApple: (() -> Unit)?,
    onContinueAsGuest: () -> Unit,
    onForgotPassword: (email: String) -> Unit,
    modifier: Modifier = Modifier,
    isLoading: Boolean = false,
    status: SignInStatus? = null,
    // Called when the user edits a field, so the host can clear a stale status banner as they retry.
    onFieldEdited: () -> Unit = {},
    onBack: (() -> Unit)? = null,
) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var passwordVisible by remember { mutableStateOf(false) }

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(AppTheme.colors.background)
            .safeContentPadding(),
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = AppTheme.spacing.lg, vertical = AppTheme.spacing.xxl),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            Column(
                modifier = Modifier.widthIn(max = AppTheme.sizes.contentMaxWidthCompact).fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                // Logo + heading — the Mahaleh mark on brand ink, matching Welcome and the app icon.
                Box(
                    modifier = Modifier
                        .background(AppTheme.colors.brandInk, AppTheme.shapes.tile)
                        .padding(AppTheme.spacing.md),
                    contentAlignment = Alignment.Center,
                ) {
                    BrandMark(
                        contentDescription = stringResource(Res.string.signin_logo_a11y),
                        modifier = Modifier.size(AppTheme.sizes.iconHero),
                    )
                }
                Spacer(Modifier.height(AppTheme.spacing.lg))
                Text(
                    text = stringResource(Res.string.signin_brand),
                    style = AppTheme.typography.headlineLg,
                    color = AppTheme.colors.primary,
                    textAlign = TextAlign.Center,
                )
                Spacer(Modifier.height(AppTheme.spacing.sm))
                Text(
                    text = stringResource(Res.string.signin_subtitle),
                    style = AppTheme.typography.bodyMd,
                    color = AppTheme.colors.onSurfaceVariant,
                    textAlign = TextAlign.Center,
                )

                Spacer(Modifier.height(AppTheme.spacing.xl))

                // Login card
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .shadow(AppTheme.elevation.level2, AppTheme.shapes.tile)
                        .background(AppTheme.colors.surface, AppTheme.shapes.tile)
                        .padding(AppTheme.spacing.xl),
                    verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
                ) {
                    // Email
                    FieldLabel(icon = "mail", text = stringResource(Res.string.signin_email_label))
                    AppTextField(
                        value = email,
                        onValueChange = { email = it; onFieldEdited() },
                        placeholder = stringResource(Res.string.signin_email_placeholder),
                        keyboardType = KeyboardType.Email,
                    )

                    // Password
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        FieldLabel(icon = "lock", text = stringResource(Res.string.signin_password_label))
                        Text(
                            text = stringResource(Res.string.signin_forgot),
                            style = AppTheme.typography.labelMd,
                            color = AppTheme.colors.primary,
                            modifier = Modifier.clickable { onForgotPassword(email) },
                        )
                    }
                    AppTextField(
                        value = password,
                        onValueChange = { password = it; onFieldEdited() },
                        placeholder = stringResource(Res.string.signin_password_placeholder),
                        keyboardType = KeyboardType.Password,
                        visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                        trailing = {
                            AppIconButton(
                                iconName = if (passwordVisible) "visibility_off" else "visibility",
                                contentDescription = stringResource(Res.string.signin_show_password_a11y),
                                onClick = { passwordVisible = !passwordVisible },
                                tint = AppTheme.colors.onSurfaceVariant,
                            )
                        },
                    )

                    AppButton(
                        text = stringResource(Res.string.signin_submit),
                        onClick = { onSignIn(email, password) },
                        modifier = Modifier.fillMaxWidth(),
                        trailingIcon = "arrow_forward",
                        loading = isLoading,
                    )

                    // Inline auth status (error in red; reset-sent confirmation in primary)
                    if (status != null) {
                        StatusBanner(status)
                    }

                    // Divider + social — only when at least one wired provider exists.
                    if (onGoogle != null || onApple != null) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Divider(Modifier.weight(1f))
                        Text(
                            text = stringResource(Res.string.signin_divider).uppercase(),
                            style = AppTheme.typography.labelSm,
                            color = AppTheme.colors.onSurfaceVariant,
                            modifier = Modifier.padding(horizontal = AppTheme.spacing.md),
                        )
                        Divider(Modifier.weight(1f))
                    }

                    Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg)) {
                        if (onGoogle != null) AppButton(
                            text = stringResource(Res.string.signin_google),
                            onClick = onGoogle,
                            modifier = Modifier.weight(1f),
                            variant = AppButtonVariant.Secondary,
                            enabled = !isLoading,
                            leadingContent = { GoogleLogo() },
                        )
                        if (onApple != null) AppButton(
                            text = stringResource(Res.string.signin_apple),
                            onClick = onApple,
                            modifier = Modifier.weight(1f),
                            variant = AppButtonVariant.Secondary,
                            leadingContent = { AppleLogo() },
                            enabled = !isLoading,
                        )
                    }
                    }

                    // Guest
                    Row(
                        modifier = Modifier.fillMaxWidth().clickable(onClick = onContinueAsGuest),
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = stringResource(Res.string.signin_continue_guest),
                            style = AppTheme.typography.labelMd,
                            color = AppTheme.colors.primary,
                        )
                        AppIcon(name = "chevron_right", size = AppTheme.sizes.iconButton, tint = AppTheme.colors.primary)
                    }
                }

                Spacer(Modifier.height(AppTheme.spacing.xl))

                // Join now
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs)) {
                    Text(
                        text = stringResource(Res.string.signin_new_here),
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.onSurfaceVariant,
                    )
                    Text(
                        text = stringResource(Res.string.signin_join_now),
                        style = AppTheme.typography.labelMd,
                        color = AppTheme.colors.primary,
                        modifier = Modifier.clickable(onClick = onSignUp),
                    )
                }
            }
        }

        // Floating "Verified Persian Business Network" pill. Hidden while the keyboard is up —
        // safeContentPadding includes IME insets, so a bottom-aligned pill would ride the keyboard
        // straight over the password field. (`isImeVisible` is Android-only and broke the JS
        // target; the ime-inset bottom > 0 check is the cross-platform equivalent.)
        val imeVisible = WindowInsets.ime
            .getBottom(androidx.compose.ui.platform.LocalDensity.current) > 0
        if (!imeVisible) Row(
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .padding(bottom = AppTheme.spacing.xl)
                .background(AppTheme.colors.secondaryContainer, AppTheme.shapes.pill)
                .padding(horizontal = AppTheme.spacing.lg, vertical = AppTheme.spacing.sm),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            AppIcon(name = "stars", size = AppTheme.sizes.iconButton, tint = AppTheme.colors.onSecondaryContainer)
            Text(
                text = stringResource(Res.string.signin_network_badge),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSecondaryContainer,
            )
        }
        // Drawn last so it sits ABOVE the full-screen scroll Column (same fix as SignUp); without it
        // Welcome→SignIn was a dead end on web/iOS where browser/system back doesn't apply.
        if (onBack != null) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.signup_back_a11y),
                onClick = onBack,
                modifier = Modifier.align(Alignment.TopStart),
            )
        }
    }
}

@Composable
internal fun StatusBanner(status: SignInStatus) {
    val isError = status != SignInStatus.ResetSent
    val message = when (status) {
        SignInStatus.InvalidCredentials -> stringResource(Res.string.signin_error_invalid_credentials)
        SignInStatus.InvalidEmail -> stringResource(Res.string.signin_error_invalid_email)
        SignInStatus.EmailInUse -> stringResource(Res.string.signin_error_email_in_use)
        SignInStatus.Network -> stringResource(Res.string.signin_error_network)
        SignInStatus.Generic -> stringResource(Res.string.signin_error_generic)
        SignInStatus.ResetSent -> stringResource(Res.string.signin_reset_sent)
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                if (isError) AppTheme.colors.errorContainer else AppTheme.colors.secondaryContainer,
                AppTheme.shapes.sm,
            )
            .padding(AppTheme.spacing.md),
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        AppIcon(
            name = if (isError) "error" else "mark_email_read",
            size = AppTheme.sizes.iconSm,
            tint = if (isError) AppTheme.colors.onErrorContainer else AppTheme.colors.onSecondaryContainer,
        )
        Text(
            text = message,
            style = AppTheme.typography.bodySm,
            color = if (isError) AppTheme.colors.onErrorContainer else AppTheme.colors.onSecondaryContainer,
        )
    }
}

@Composable
private fun FieldLabel(icon: String, text: String) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
        AppIcon(name = icon, size = AppTheme.sizes.iconSm, tint = AppTheme.colors.onSurfaceVariant)
        Text(text = text, style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurfaceVariant)
    }
}

@Composable
internal fun Divider(modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .height(AppTheme.sizes.borderWidth)
            .background(AppTheme.colors.outlineVariant),
    )
}

@Preview
@Composable
private fun SignInScreenPreview() {
    AppTheme {
        SignInScreen(
            onSignIn = { _, _ -> },
            onSignUp = {},
            onGoogle = null,
            onApple = null,
            onContinueAsGuest = {},
            onForgotPassword = {},
        )
    }
}
